import graphql from "babel-plugin-relay/macro";
import { groupBy, partition } from "lodash";
import { useFragment } from "react-relay";

import { getFacilityMemberPermissions } from "@/hooks/getFacilityMemberPermissions";
import { useFlags } from "@/utils/launchDarkly";

import { departmentToScheduleOption } from "../helpers/departmentToScheduleOption";
import { facilityToScheduleOption } from "../helpers/facilityToScheduleOption";
import {
  DepartmentType,
  useGetScheduleSwitcherOptionsFragment_session$key,
} from "./__generated__/useGetScheduleSwitcherOptionsFragment_session.graphql";

export type ScheduleOption = {
  type: "facility" | "department";
  label: string;
  id: string;
  slug: string;
  departmentType: DepartmentType;
  facilityIds?: string[];
};

export const useGetScheduleSwitcherOptions = (
  sessionRef: useGetScheduleSwitcherOptionsFragment_session$key | null
) => {
  const { patronDepartmentAvailability } = useFlags();

  const session = useFragment(
    useGetScheduleSwitcherOptionsSessionFragmentString,
    sessionRef
  );

  const club = session?.currentClientProfile?.member?.club;
  const facilities =
    club?.facilities?.edges?.flatMap((f) => (f ? [f.node] : [])) || [];

  const accessibleFacilities = facilities.filter(
    (facility) =>
      "FACILITY_PERMISSIONS_DISABLED" !==
      getFacilityMemberPermissions({ fragmentRef: facility ?? null })
        .memberAccess
  );

  const [golfCourseFacilities, nonGolfFacilities] = partition(
    accessibleFacilities,
    (f) => f?.type === "GOLF_COURSE"
  );

  const [diningFacilities, otherFacilities] = partition(
    nonGolfFacilities,
    (f) => f?.type === "DINING"
  );

  const golfCourseMap = groupBy(golfCourseFacilities, (f) => f?.department?.id);
  const diningMap = groupBy(diningFacilities, (f) => f?.department?.id);

  const getFacilityIds = (
    optionFacilities: typeof accessibleFacilities
  ): string[] =>
    optionFacilities.flatMap((facility) => (facility?.id ? [facility.id] : []));

  const facilityToScheduleOptionWithIds = (
    facility: (typeof accessibleFacilities)[number] | null
  ): ScheduleOption => ({
    ...facilityToScheduleOption(facility ?? null),
    facilityIds: facility?.id ? [facility.id] : [],
  });

  const golfCourseOptions: ScheduleOption[] = Object.values(
    golfCourseMap
  ).flatMap((golfCourses) =>
    golfCourses.length > 1 && patronDepartmentAvailability
      ? [
          {
            ...departmentToScheduleOption(golfCourses[0]?.department || null),
            facilityIds: getFacilityIds(golfCourses),
          },
        ]
      : golfCourses.map((facility) =>
          facilityToScheduleOptionWithIds(facility ?? null)
        )
  );

  const diningFacilitiesOptions: ScheduleOption[] = Object.values(
    diningMap
  ).flatMap((dining) => facilityToScheduleOptionWithIds(dining[0] ?? null));

  const otherFacilityOptions = otherFacilities.map((facility) =>
    facilityToScheduleOptionWithIds(facility ?? null)
  );

  const options = [
    ...golfCourseOptions,
    ...otherFacilityOptions,
    ...diningFacilitiesOptions,
  ];

  return options;
};

export const useGetScheduleSwitcherOptionsSessionFragmentString = graphql`
  fragment useGetScheduleSwitcherOptionsFragment_session on Session {
    currentClientProfile {
      member {
        club {
          facilities {
            edges {
              node {
                type
                id
                ...getFacilityMemberPermissions_facility
                ...facilityToScheduleOption_facility
                department {
                  id
                  type
                  ...departmentToScheduleOption_department
                }
              }
            }
          }
        }
      }
    }
  }
`;
